import React, { useState, useEffect, useRef } from "react";
import ApexCharts from "apexcharts";
import Chart from "react-apexcharts";
declare const window: any;
// Assign ApexCharts to the global window object
window.ApexCharts = ApexCharts;
import axiosInstance from "src/common/axiosInstance";
import { useSelector } from "react-redux";
import { RootState } from "../../slices/store";
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip } from "reactstrap";
import { GiEmptyHourglass } from "react-icons/gi";
import { FiTable } from "react-icons/fi";
import UnderlyingdataModal from "./UnderlyingdataModal";

// Hook to check if screen is mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480); // 6.5 inch phones are around 480px
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};


const UserChart = () => {
  const [loading, setLoading] = useState(true);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const filters = useSelector((state: RootState) => state.filters);
  const { dataBy, isTopNavReady } = filters;
  const userState = useSelector((state: RootState) => state.login);
  const fetchId = useRef(0);
  const [selectedMetric, setSelectedMetric] = useState("Served__c");
  const [selectedMetric2, setSelectedMetric2] = useState("Clicks__c");
  const isMobile = useIsMobile();

  const [label, setLabel] = useState("Impressions");
  const [label2, setLabel2] = useState("Clicks");

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  // Cleanup chart instances on unmount to prevent stale references
  useEffect(() => {
    return () => {
      try {
        const primaryChart = window.ApexCharts?.getChartByID('primaryChart');
        const brushChart = window.ApexCharts?.getChartByID('brushChart');
        if (brushChart) brushChart.destroy();
        if (primaryChart) primaryChart.destroy();
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  }, []);

  // Enhanced number formatting functions
  const formatNumber = (value: number): string => {
    if (value >= 1000000000) {
      return (value / 1000000000).toFixed(1) + 'B';
    } else if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'K';
    }
    return Math.round(value).toLocaleString();
  };

  const formatCurrency = (value: number): string => {
    if (value >= 1000000000) {
      return '$' + (value / 1000000000).toFixed(1) + 'B';
    } else if (value >= 1000000) {
      return '$' + (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return '$' + (value / 1000).toFixed(1) + 'K';
    } else if (value >= 100) {
      return '$' + Math.round(value);
    }
    return '$' + value.toFixed(2);
  };

  const formatPercentage = (value: number): string => {
    if (value >= 100) {
      return `${Math.round(value)}%`;
    }
    return `${value.toFixed(2)}%`;
  };

  const getFormattedValue = (value: number, seriesName: string): string => {
    const lowerName = seriesName.toLowerCase();
    
    // Check for percentage metrics first - comprehensive detection for CTR, CVR, and rates
    if ((lowerName.includes('ctr') || lowerName.includes('cvr') || lowerName.includes('rate') || 
        lowerName.includes('%') || lowerName.includes('conv') || 
        lowerName.includes('avg. cvr') || lowerName.includes('avg cvr') ||
        lowerName.includes('avg. ctr') || lowerName.includes('avg ctr') ||
        lowerName === 'ctr' || lowerName === 'cvr')&& 
        !lowerName.includes('conversion')) {
      return formatPercentage(value);
    }
    // Check for currency metrics
    else if (lowerName.includes('spend') || lowerName.includes('cost') || lowerName.includes('cpc') || lowerName.includes('cpa')) {
      return formatCurrency(value);
    } 
    // Explicitly handle impressions, clicks, reach with abbreviated formatting
    else if (lowerName.includes('impression') || lowerName.includes('click') || lowerName.includes('reach') || lowerName.includes('view')) {
      return formatNumber(value);
    }
    // Default to number formatting with abbreviations for large numbers
    else {
      return formatNumber(value);
    }
  };
  // Get responsive chart heights
  const primaryChartHeight = isMobile ? 250 : 350;
  const brushChartHeight = isMobile ? 120 : 200;
  
  // Update chart options when mobile state changes
  useEffect(() => {
    setChartOptions(prev => ({
      ...prev,
      primaryChart: {
        ...prev.primaryChart,
        chart: {
          ...prev.primaryChart.chart,
          height: primaryChartHeight,
          zoom: { 
            enabled: isMobile,
            type: 'x',
            autoScaleYaxis: true
          }
        }
      },
      brushChart: {
        ...prev.brushChart,
        chart: {
          ...prev.brushChart.chart,
          height: brushChartHeight,
          toolbar: { show: false },
          brush: { target: "primaryChart", enabled: !isMobile },
          selection: {
            enabled: true,
            xaxis: {
              min: undefined,
              max: undefined,
            },
            fill: {
              color: '#0d6efd',
              opacity: 0.2
            },
            stroke: {
              width: 1,
              color: '#0d6efd',
              opacity: 0.8,
              dashArray: 3
            },
          },
          offsetX: 0
        }
      }
    }));
  }, [isMobile, primaryChartHeight, brushChartHeight]);

  const [chartOptions, setChartOptions] = useState({
    primaryChart: {
      chart: {
        id: "primaryChart",
        type: "line",
        height: primaryChartHeight,
        toolbar: { show: false },
        zoom: {
          enabled: isMobile,
          type: 'x',
          autoScaleYaxis: true
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 100
          },
          dynamicAnimation: {
            enabled: true,
            speed: 400
          }
        },
        events: {
          legendClick: function(chartContext: any, seriesIndex: any, config: any) {
            return false; // Prevent legend click
          }
        }
      },
      xaxis: dataBy === 'day' ? {
        // For daily data, use datetime type
        type: "datetime",
        labels: {
          formatter: function (value: string) {
            const date = new Date(value);
            return date.toISOString().split("T")[0];
          },
        },
        min: undefined,
        max: undefined
      } : {
        // For aggregated data (week, month, quarter, year), use categories
        type: "category",
        categories: data.map((item: any) => item.x),
        labels: {
          rotate: -55,
          rotateAlways: false,
          trim: false,
          style: {
            fontSize: '11px',
          }
        },
        tickAmount: Math.min(data.length - 1, 12),
        tickPlacement: 'on',
      },
      stroke: { 
        curve: "smooth",
        width: 3,
        lineCap: 'round',
        dashArray: 0
      },
      legend: {
        onItemClick: {
          toggleDataSeries: false
        }
      },
      series: [
        { name: label, data: [] }, // Placeholder for y data
        { name: label2, data: [] }, // Placeholder for z data
      ],
      yaxis: [
        {
          title: { text: label },
          labels: {
            minWidth: 40,
            formatter: (value: number) => getFormattedValue(value, label),
          },
        },
        {
          opposite: true,
          title: { text: label2 },
          labels: {
            minWidth: 40,
            formatter: (value: number) => getFormattedValue(value, label2),
          },
        },
      ],
      tooltip: {
        shared: true,
        intersect: false,
        y: [
          {
            formatter: function (val: number, opts: any) {
              const seriesName = opts.w.config.series[opts.seriesIndex].name;
              return getFormattedValue(val, seriesName);
            }
          },
          {
            formatter: function (val: number, opts: any) {
              const seriesName = opts.w.config.series[opts.seriesIndex].name;
              return getFormattedValue(val, seriesName);
            }
          }
        ]
      },
    },
    brushChart: {
      chart: {
        id: "brushChart",
        type: "area",
        height: brushChartHeight,
        toolbar: { show: false },
        brush: { target: "primaryChart", enabled: !isMobile },
        events: {
          zoomed: function(chartContext: any, { xaxis }: any) {
            const primaryChart = window.ApexCharts?.getChartByID('primaryChart');
            if (!primaryChart) return;
          }
        },
        selection: {
          enabled: true,
          xaxis: {
            min: undefined,
            max: undefined,
          },
          fill: {
            color: '#0d6efd',
            opacity: 0.2
          },
          stroke: {
            width: 1,
            color: '#0d6efd',
            opacity: 0.8,
            dashArray: 3
          },
        },
        offsetX: 0,
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 700,
          animateGradually: {
            enabled: true,
            delay: 120
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        },
      },
      xaxis: dataBy === 'day' ? {
        // For daily data, use datetime type
        type: "datetime",
        labels: {
          show: true,
          formatter: function (value: string) {
            const date = new Date(value);
            return date.toISOString().split("T")[0];
          },
        },
        axisBorder: { show: true },
        axisTicks: { show: true }
      } : {
        // For aggregated data (week, month, quarter, year), use categories
        type: "category",
        categories: data.map((item: any) => item.x),
        labels: {
          show: true,
          rotate: -60,
          rotateAlways: false,
          trim: false,
          maxHeight: 100,
          style: {
            fontSize: '10px',
          }
        },
        axisBorder: { show: true },
        axisTicks: { show: true },
        tickAmount: Math.min(data.length - 1, 12),
        tickPlacement: 'on',
      },
      yaxis: [
        {
          show: true,
          showAlways: true,
          title: { text: undefined },
          labels: {
            show: false,
            minWidth: 40,
            formatter: (value: number) => getFormattedValue(value, label),
          },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        {
          show: true,
          showAlways: true,
          opposite: true,
          title: { text: undefined },
          labels: {
            show: false,
            minWidth: 40,
            formatter: (value: number) => getFormattedValue(value, label2),
          },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
      ],
      legend: { 
        show: false,
        onItemClick: {
          toggleDataSeries: false
        }
      },
      tooltip: { enabled: false },
      dataLabels: { enabled: false },
      grid: {
        show: false,
        padding: {
          bottom: 20
        }
      },
      stroke: { 
        show: true, 
        width: 2,
        curve: "smooth",
        lineCap: 'round'
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      series: [
        { name: label, data: [] },
        { name: label2, data: [] },
      ],
    },
  });
  const allMetricOptions = [
    { value: "Served__c", label: "Impressions", widgetTitle: "Impressions" },
    { value: "Clicks__c", label: "Clicks", widgetTitle: "Clicks" },
    { value: "Conversions__c", label: "Conversions", widgetTitle: null },
    { value: "Actual_Billable__c", label: "Est. Spend", widgetTitle: "Est. Spend" },
    { value: "Completed_Views__c", label: "Completed Views", widgetTitle: null },
    { value: "CTR__c", label: "CTR", widgetTitle: "AVG. CTR" },
  ];

  // Filter metric options based on analytics_widgets permissions
  const analyticsWidgetsRaw = userState.user?.user?.analytics_widgets as string | undefined;
  const allowedWidgets: string[] | null = analyticsWidgetsRaw ? JSON.parse(analyticsWidgetsRaw) : null;

  const metricOptions = allMetricOptions.filter(option =>
    !option.widgetTitle || !allowedWidgets || allowedWidgets.includes(option.widgetTitle)
  );

  // Prevent selecting same metric in both dropdowns
  const getAvailableOptions = (currentMetric: string, isSecondDropdown: boolean) => {
    if (isSecondDropdown) {
      return metricOptions.filter(option => option.value !== selectedMetric);
    } else {
      return metricOptions.filter(option => option.value !== selectedMetric2);
    }
  };

  // Fetch data from Laravel backend
  useEffect(() => {
    if (!isTopNavReady) {
      setLoading(true);
      return;
    }

    setLoading(true);
    const fetchData = async () => {
      const currentRequestId = ++fetchId.current;
      try {
        let data: any;
        const response = await axiosInstance.get<any[]>(`/bchart-data`, {
          params: {
            id: filters.selectedOrder,
            start_date: filters.startDate,
            end_date: filters.endDate,
            lineItems: filters.selectedLineItems,
            mediaType: filters.selectedMediaType,
            rateType: filters.selectedRateType,
            exchangeType: filters.selectedExchangeType,
            adFormat: filters.selectedAdFormat,
            selectedMetric: selectedMetric,
            selectedMetric2: selectedMetric2,
            dataBy: dataBy
          },
        });
        data = response.data;

        // For category type (aggregated data), only pass y values for PRIMARY chart
        // For datetime type (daily), pass {x, y} objects
        const chartDataY = dataBy === 'day'
          ? data.map((item: any) => ({ x: item.x, y: item.y }))
          : data.map((item: any) => item.y);

        const chartDataZ = dataBy === 'day'
          ? data.map((item: any) => ({ x: item.x, y: item.z }))
          : data.map((item: any) => item.z);

        // For BRUSH chart, MUST match PRIMARY chart format for proper synchronization
        const brushDataY = dataBy === 'day'
          ? data.map((item: any) => ({ x: item.x, y: item.y }))
          : data.map((item: any) => item.y);

        const brushDataZ = dataBy === 'day'
          ? data.map((item: any) => ({ x: item.x, y: item.z }))
          : data.map((item: any) => item.z);

        // Check if we have any meaningful chart data
        const hasData = response.data && response.data.length > 0 &&
                       response.data.some((item: any) => (item.y && item.y > 0) || (item.z && item.z > 0));

        if (currentRequestId === fetchId.current) {
          // Update all states together to prevent flash and stale data
          setData(data);
          setNoDataAvailable(hasData);
          setChartOptions((prev) => ({
            ...prev,
            primaryChart: {
              ...prev.primaryChart,
              series: [
                { name: label, data: chartDataY },
                { name: label2, data: chartDataZ },
              ],
              xaxis: dataBy === 'day' ? {
                type: "datetime",
                labels: {
                  formatter: function (value: string) {
                    const date = new Date(value);
                    return date.toISOString().split("T")[0];
                  },
                },
                min: undefined,
                max: undefined
              } : {
                type: "category",
                categories: data.map((item: any) => item.x),
                labels: {
                  rotate: -55,
                  rotateAlways: false,
                  trim: false,
                  style: {
                    fontSize: '11px',
                  }
                },
                tickAmount: Math.min(data.length - 1, 12),
                tickPlacement: 'on',
              },
              tooltip: {
                ...prev.primaryChart.tooltip,
                y: [
                  {
                    formatter: function (val: number, opts: any) {
                      if (val === null || val === undefined) return '0';
                      const seriesName = opts.w.config.series[opts.seriesIndex].name.toLowerCase();

                      // Check for spend metrics - show with $ and commas but no abbreviation
                      if (seriesName.includes('spend') || seriesName.includes('cost') || seriesName.includes('cpc') || seriesName.includes('cpa')) {
                        return '$' + val.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
                      }
                      // Check for percentage metrics - show with % and full precision
                      else if (seriesName.includes('ctr') || seriesName.includes('cvr') || seriesName.includes('rate')) {
                        return val.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 4}) + '%';
                      }
                      // For other metrics (impressions, clicks) - show with commas, no decimals
                      else {
                        return val.toLocaleString('en-US', {maximumFractionDigits: 0});
                      }
                    }
                  },
                  {
                    formatter: function (val: number, opts: any) {
                      if (val === null || val === undefined) return '0';
                      const seriesName = opts.w.config.series[opts.seriesIndex].name.toLowerCase();

                      // Check for spend metrics - show with $ and commas but no abbreviation
                      if (seriesName.includes('spend') || seriesName.includes('cost') || seriesName.includes('cpc') || seriesName.includes('cpa')) {
                        return '$' + val.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
                      }
                      // Check for percentage metrics - show with % and full precision
                      else if (seriesName.includes('ctr') || seriesName.includes('cvr') || seriesName.includes('rate')) {
                        return val.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 4}) + '%';
                      }
                      // For other metrics (impressions, clicks) - show with commas, no decimals
                      else {
                        return val.toLocaleString('en-US', {maximumFractionDigits: 0});
                      }
                    }
                  }
                ]
              },
              yaxis: [
                {
                  ...prev.primaryChart.yaxis?.[0],
                  title: { text: label },
                  labels: {
                    minWidth: 40,
                    formatter: (value: number) => getFormattedValue(value, label),
                  },
                },
                {
                  ...prev.primaryChart.yaxis?.[1],
                  title: { text: label2 },
                  opposite: true,
                  labels: {
                    minWidth: 40,
                    formatter: (value: number) => getFormattedValue(value, label2),
                  },
                },
              ],
            },
            brushChart: {
              ...prev.brushChart,
              series: [
                { name: label, data: brushDataY },
                { name: label2, data: brushDataZ },
              ],
              xaxis: dataBy === 'day' ? {
                type: "datetime",
                labels: {
                  show: true,
                  formatter: function (value: string) {
                    const date = new Date(value);
                    return date.toISOString().split("T")[0];
                  },
                },
                axisBorder: { show: true },
                axisTicks: { show: true }
              } : {
                type: "category",
                categories: data.map((item: any) => item.x),
                labels: {
                  show: true,
                  rotate: -60,
                  rotateAlways: false,
                  trim: false,
                  maxHeight: 100,
                  style: {
                    fontSize: '10px',
                  }
                },
                axisBorder: { show: true },
                axisTicks: { show: true },
                tickAmount: Math.min(data.length - 1, 12),
                tickPlacement: 'on',
              },
              yaxis: [
                {
                  ...prev.brushChart.yaxis?.[0],
                  show: true,
                  showAlways: true,
                  title: { text: undefined },
                  labels: {
                    show: false,
                    minWidth: 40,
                    formatter: (value: number) => getFormattedValue(value, label),
                  },
                  axisBorder: { show: false },
                  axisTicks: { show: false }
                },
                {
                  ...prev.brushChart.yaxis?.[1],
                  show: true,
                  showAlways: true,
                  opposite: true,
                  title: { text: undefined },
                  labels: {
                    show: false,
                    minWidth: 40,
                    formatter: (value: number) => getFormattedValue(value, label2),
                  },
                  axisBorder: { show: false },
                  axisTicks: { show: false }
                },
              ],
            },
          }));
          setLoading(false);
        } 

      } catch (error) {
        console.error("Error fetching chart data:", error);
        setLoading(false);
      }
    };
    
    if(filters.selectedOrder && filters.selectedLineItems && filters.selectedLineItems.trim() !== ''){
        fetchData();
    } else {
        // Clear data and reset chart when no order or line items are selected
        setData([]);
        setNoDataAvailable(false);
        setLoading(false);
        // Reset chart to default/empty state
        setChartOptions((prev) => ({
          ...prev,
          primaryChart: {
            ...prev.primaryChart,
            series: [],
          },
          brushChart: {
            ...prev.brushChart,
            series: [],
          },
        }));
    }
  }, [isTopNavReady, label, label2, selectedMetric, selectedMetric2, filters.selectedLineItems, filters.startDate, filters.endDate, filters.selectedMediaType, filters.selectedRateType, filters.selectedExchangeType, filters.selectedAdFormat, dataBy, userState]);
  
  useEffect(() => {
    setTimeout(() => {
      const primaryChart = window.ApexCharts.getChartByID('primaryChart');
      const brushChart = window.ApexCharts.getChartByID('brushChart');
      
      if (primaryChart && primaryChart.w.config.series) {
        // Force show all series
        primaryChart.w.config.series.forEach((series: any, index: number) => {
          if (primaryChart.w.globals.collapsedSeriesIndices.includes(index)) {
            primaryChart.toggleSeries(series.name);
          }
        });
        // Ensure all series are visible by clearing the collapsed array
        primaryChart.w.globals.collapsedSeriesIndices = [];
      }
      
      if (brushChart && brushChart.w.config.series) {
        // Force show all series
        brushChart.w.config.series.forEach((series: any, index: number) => {
          if (brushChart.w.globals.collapsedSeriesIndices.includes(index)) {
            brushChart.toggleSeries(series.name);
          }
        });
        // Ensure all series are visible by clearing the collapsed array
        brushChart.w.globals.collapsedSeriesIndices = [];
      }
    }, 300); // Slightly longer delay
  }, [filters.startDate, filters.endDate, filters.selectedLineItems, filters.selectedMediaType, filters.selectedRateType, filters.selectedExchangeType]);

  // Helper function to get date column name based on dataBy
  const getDateColumnName = (dataBy: string): string => {
    switch (dataBy) {
      case 'day': return 'Date';
      case 'week': return 'Week';
      case 'month': return 'Month';
      case 'quarter': return 'Quarter';
      case 'year': return 'Year';
      default: return 'Date';
    }
  };

  // Prepare modal data (transform API response to modal format)
  const modalData = data.map((item: any) => ({
    date: item.x,
    metric1: item.y,
    metric2: item.z
  }));

  // Format values for modal (without K/M/B abbreviations)
  const formatModalValue = (value: number, metricName: string): string => {
    const lowerName = metricName.toLowerCase();

    // Check for percentage metrics
    if ((lowerName.includes('ctr') || lowerName.includes('cvr') || lowerName.includes('rate') ||
        lowerName.includes('%') || lowerName.includes('conv') ||
        lowerName.includes('avg. cvr') || lowerName.includes('avg cvr') ||
        lowerName.includes('avg. ctr') || lowerName.includes('avg ctr') ||
        lowerName === 'ctr' || lowerName === 'cvr') &&
        !lowerName.includes('conversion')) {
      return value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 4}) + '%';
    }
    // Check for currency metrics
    else if (lowerName.includes('spend') || lowerName.includes('cost') || lowerName.includes('cpc') || lowerName.includes('cpa')) {
      return '$' + value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    }
    // All other metrics - show actual values with commas
    else {
      return Math.round(value).toLocaleString('en-US');
    }
  };

  // Define modal columns configuration
  const modalColumns = [
    {
      header: getDateColumnName(dataBy),
      accessor: 'date',
    },
    {
      header: label,
      accessor: 'metric1',
      formatter: (value: number) => formatModalValue(value, label)
    },
    {
      header: label2,
      accessor: 'metric2',
      formatter: (value: number) => formatModalValue(value, label2)
    }
  ];

  return (
    <>
      <div className="col-lg-12">
        <div className="card h-100 analytics-card">
          <div className="card-body" style={{
            paddingTop: "14px",
            padding: isMobile ? "10px" : "20px"
          }}>
            {loading ? (
              <div className="loading-indicator" style={{ height: `${primaryChartHeight + brushChartHeight + 120}px`, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <div className="loading-hourglass">
                  {(GiEmptyHourglass as unknown as React.FC<any>)({ size: 24 })}
                </div>
                <div className="loading-text text-center text-muted mt-3">
                  <h6>Fetching data...</h6>
                  <p className="small">Please wait while we load your analytics.</p>
                </div>
              </div>
            ) : (
              <>
                <div className={`d-flex align-items-center ${isMobile ? 'flex-column' : 'justify-content-between'}`} style={{ gap: isMobile ? "8px" : "10px" }}>
                  {/* Hover Icon for View Data */}
                  {noDataAvailable && (
                    <div
                      className="underlying-data-icon"
                      onClick={toggleModal}
                      style={{
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="View underlying data"
                    >
                      {(FiTable as unknown as React.FC<any>)({ size: 16, color: '#6c757d' })}
                    </div>
                  )}

                  <div className="d-flex align-items-center" style={{ gap: "10px", marginLeft: 'auto' }}>
                    <UncontrolledDropdown className="widget-dropdown">
                      <DropdownToggle
                        id="primary-metric-dropdown"
                        className="btn"
                        tag="a"
                        style={{ cursor: 'pointer' }}
                      >
                        {metricOptions.find(opt => opt.value === selectedMetric)?.label || "Select Metric"}
                        <i className="mdi mdi-chevron-down"></i>
                      </DropdownToggle>
                    <UncontrolledTooltip
                      placement="top"
                      target="primary-metric-dropdown"
                      innerClassName="custom-tooltip-inner"
                    >
                      Select the main value displayed on the chart&apos;s primary axis.
                    </UncontrolledTooltip>
                    <DropdownMenu>
                      {getAvailableOptions(selectedMetric, false).map((option) => (
                        <DropdownItem
                          className={selectedMetric == option.value ? 'selected' : ""}
                          key={option.value}
                          onClick={() => {
                            setSelectedMetric(option.value);
                            setLabel(option.label);
                          }}
                        >
                          {option.label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </UncontrolledDropdown>

                  <UncontrolledDropdown className="widget-dropdown">
                    <DropdownToggle
                      id="secondary-metric-dropdown"
                      className="btn"
                      tag="a"
                      style={{ cursor: 'pointer' }}
                    >
                      {metricOptions.find(opt => opt.value === selectedMetric2)?.label || "Select Metric"}
                      <i className="mdi mdi-chevron-down"></i>
                    </DropdownToggle>
                    <UncontrolledTooltip
                      placement="top"
                      target="secondary-metric-dropdown"
                      innerClassName="custom-tooltip-inner"
                    >
                      Select an additional value to display on the chart&apos;s secondary axis.
                    </UncontrolledTooltip>
                    <DropdownMenu>
                      {getAvailableOptions(selectedMetric2, true).map((option) => (
                        <DropdownItem
                          className={selectedMetric2 == option.value ? 'selected' : ""}
                          key={option.value}
                          onClick={() => {
                            setSelectedMetric2(option.value);
                            setLabel2(option.label);
                          }}
                        >
                          {option.label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                  </div>
                </div>
                {!noDataAvailable ? (
                  <div className="loading-indicator" style={{ height: `${primaryChartHeight + brushChartHeight + 120}px`, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                    <div>
                      {(GiEmptyHourglass as unknown as React.FC<any>)({ size: 28 })}
                    </div>
                    <div className="loading-text text-center text-muted mt-3">
                      <h6>No data found for applied filters.</h6>
                      <p className="small">Please change filters to see data.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div id="primary_chart" style={{ textAlign: "end", overflow: "hidden" }}>
                      <Chart
                        key={`primary-${dataBy}`}
                        options={chartOptions.primaryChart}
                        series={chartOptions.primaryChart.series}
                        type="line"
                        height={primaryChartHeight}
                      />
                    </div>
                    {!isMobile && (
                      <div id="brush_chart" style={{ overflow: "hidden" }}>
                        <Chart
                          key={`brush-${dataBy}`}
                          options={chartOptions.brushChart}
                          series={chartOptions.brushChart.series}
                          type="area"
                          height={brushChartHeight}
                        />
                      </div>
                    )}
                    {isMobile && (
                      <div style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginTop: '10px' }}>
                        Pinch to zoom on mobile
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Underlying Data Modal */}
      <UnderlyingdataModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        kpiTitle="Chart Data"
        kpiData={modalData}
        dataBy={dataBy}
        columns={modalColumns}
        title="Brush Chart"
        hideInfoAlert={true}
      />
    </>
  );
};

export default UserChart;
